import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Mark } from '@tiptap/core';
import { useState, useEffect, useRef } from 'react';
import returnIcon from '@/assets/svg/return_icon.svg';
import { getExhibitionThemeClassName, getExhibitionThemeStyle } from '@/features/exhibitions/utils/exhibitionColors';

export type TranscriptionType = 'text' | 'title' | 'captions';

interface TranscriptionEditorProps {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    /** Couleur de l'expo pour le thème (onglets, boutons, didascalies) */
    exhibitionColor?: string;
}

// Extension personnalisée pour les didascalies (mark inline)
const CaptionsMark = Mark.create({
    name: 'captions',

    addOptions() {
        return {
            HTMLAttributes: {
                'data-type': 'captions',
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'span[data-type="captions"]',
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return ['span', { ...this.options.HTMLAttributes, ...HTMLAttributes }, 0];
    },

});

// Extension personnalisée pour les titres (mark inline mais avec style block)
const TitleMark = Mark.create({
    name: 'title',

    addOptions() {
        return {
            HTMLAttributes: {
                'data-type': 'title',
                style: 'display: block;',
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'span[data-type="title"]',
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return ['span', { ...this.options.HTMLAttributes, ...HTMLAttributes }, 0];
    },

});

export const TranscriptionEditor: React.FC<TranscriptionEditorProps> = ({
    value,
    onChange,
    placeholder = "Merci de fournir une transcription fidèle de l'audio, incluant si possible les indications sonores (ex. : rires, musique). Les personnes sourdes ou malentendantes pourront ainsi accéder pleinement au contenu.",
    exhibitionColor,
}) => {
    const [activeType, setActiveType] = useState<TranscriptionType>('text');
    const [isBold, setIsBold] = useState(false);
    const [isItalic, setIsItalic] = useState(false);
    /** Ignorer le prochain onUpdate déclenché par setContent (évite de marquer "modifications non enregistrées" à l'ouverture) */
    const skipNextOnChangeRef = useRef(false);
    /** Annuler l’application différée de activeType = 'text' si un nouvel événement indique captions/title */
    const pendingTextRafRef = useRef<number | null>(null);

    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                // Désactiver certaines extensions par défaut si nécessaire
            }),
            CaptionsMark,
            TitleMark,
        ],
        content: value || '',
        onUpdate: ({ editor }) => {
            if (skipNextOnChangeRef.current) {
                skipNextOnChangeRef.current = false;
                return;
            }
            const html = editor.getHTML();
            if (html === (value || '')) return;
            onChange(html);
        },
        editorProps: {
            attributes: {
                class: 'transcription-content',
                'data-placeholder': placeholder,
            },
        },
    });

    // Mettre à jour le contenu quand value change de l'extérieur (ex. chargement d'une œuvre en édition)
    useEffect(() => {
        if (editor && value !== editor.getHTML()) {
            skipNextOnChangeRef.current = true;
            editor.commands.setContent(value || '');
        }
    }, [value, editor]);

    // Synchroniser la barre d'outils avec la position du curseur : onglets (Texte/Titre/Didascalies), gras, italique
    useEffect(() => {
        if (!editor) return;
        const updateToolbarState = () => {
            setIsBold(editor.isActive('bold'));
            setIsItalic(editor.isActive('italic'));
            // Détecter Texte / Titre / Didascalies via les marks à la position du curseur (ProseMirror ResolvedPos.marks())
            const $from = editor.state.selection.$from;
            const marksAtCursor = $from.marks();
            const hasCaptions = marksAtCursor.some((m) => m.type.name === 'captions');
            const hasTitle = marksAtCursor.some((m) => m.type.name === 'title');
            const nextType: TranscriptionType = hasCaptions ? 'captions' : hasTitle ? 'title' : 'text';

            if (nextType !== 'text') {
                if (pendingTextRafRef.current != null) {
                    cancelAnimationFrame(pendingTextRafRef.current);
                    pendingTextRafRef.current = null;
                }
                setActiveType(nextType);
            } else {
                pendingTextRafRef.current = requestAnimationFrame(() => {
                    pendingTextRafRef.current = null;
                    setActiveType('text');
                });
            }
        };
        updateToolbarState();
        editor.on('selectionUpdate', updateToolbarState);
        editor.on('transaction', updateToolbarState);
        return () => {
            editor.off('selectionUpdate', updateToolbarState);
            editor.off('transaction', updateToolbarState);
            if (pendingTextRafRef.current != null) {
                cancelAnimationFrame(pendingTextRafRef.current);
                pendingTextRafRef.current = null;
            }
        };
    }, [editor]);

    // Coller en texte brut (sans format) pour éviter les formats indésirables
    useEffect(() => {
        if (!editor) return;
        const el = editor.view.dom;
        const handlePaste = (e: ClipboardEvent) => {
            const text = e.clipboardData?.getData('text/plain');
            if (text != null && text !== '') {
                e.preventDefault();
                editor.chain().focus().insertContent(text).run();
            }
        };
        el.addEventListener('paste', handlePaste);
        return () => el.removeEventListener('paste', handlePaste);
    }, [editor]);

    const handleTypeChange = (type: TranscriptionType) => {
        setActiveType(type);

        if (!editor) return;

        const { state } = editor;
        const { selection } = state;

        // Si du texte est sélectionné
        if (!selection.empty) {
            // D'abord, retirer tous les marks de type existants
            if (editor.isActive('captions')) {
                editor.chain().focus().unsetMark('captions').run();
            }
            if (editor.isActive('title')) {
                editor.chain().focus().unsetMark('title').run();
            }

            // Ensuite, appliquer le nouveau type si ce n'est pas "text"
            if (type === 'captions') {
                editor.chain().focus().setMark('captions').run();
            } else if (type === 'title') {
                editor.chain().focus().setMark('title').run();
            }
        }
    };

    const handleFormat = (command: 'bold' | 'italic') => {
        if (!editor) return;

        if (command === 'bold') {
            editor.chain().focus().toggleBold().run();
        } else {
            editor.chain().focus().toggleItalic().run();
        }
    };

    const handleUndo = () => {
        editor?.chain().focus().undo().run();
    };

    const handleRedo = () => {
        editor?.chain().focus().redo().run();
    };

    const handleClearFormat = () => {
        editor?.chain().focus().unsetAllMarks().run();
    };

    if (!editor) {
        return null;
    }

    const themeClass = getExhibitionThemeClassName(exhibitionColor, 'transcription-editor');

    return (
        <div
            className={`transcription-editor ${themeClass}`}
            style={getExhibitionThemeStyle(exhibitionColor)}
        >
            <div className="transcription-header">

                <div className="transcription-tabs">
                    <button
                        className={`transcription-tab ${activeType === 'text' ? 'active' : ''}`}
                        onClick={() => handleTypeChange('text')}
                    >
                        Texte
                    </button>
                    <button
                        className={`transcription-tab ${activeType === 'title' ? 'active' : ''}`}
                        onClick={() => handleTypeChange('title')}
                    >
                        Titre
                    </button>
                    <button
                        className={`transcription-tab ${activeType === 'captions' ? 'active' : ''}`}
                        onClick={() => handleTypeChange('captions')}
                    >
                        Didascalies
                    </button>
                </div>
                <div className="formatting-toolbar">
                    <div className="toolbar-separator"></div>
                    <button
                        className={`format-btn ${isBold ? 'active' : ''}`}
                        onClick={() => handleFormat('bold')}
                        title="Gras"
                    >
                        B
                    </button>
                    <button
                        className={`format-btn ${isItalic ? 'active' : ''}`}
                        onClick={() => handleFormat('italic')}
                        title="Italique"
                    >
                        I
                    </button>
                    <button
                        className="format-btn clear-format-btn"
                        onClick={handleClearFormat}
                        title="Retirer le formatage"
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                            <path d="M10 5h4M7 19h10M12 5v14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M4 12h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                    </button>
                    <div className="toolbar-separator"></div>
                    <button
                        className="format-btn"
                        onClick={handleUndo}
                        disabled={!editor.can().undo()}
                        title="Annuler"
                    >
                        <img src={returnIcon} alt="undo" />
                    </button>
                    <button
                        className="format-btn"
                        onClick={handleRedo}
                        disabled={!editor.can().redo()}
                        title="Refaire"
                    >
                        <img src={returnIcon} alt="redo" style={{ transform: 'scaleY(-1) rotate(180deg)' }} />
                    </button>
                </div>
            </div>
            <EditorContent editor={editor} />
        </div>
    );
};
